import { createFileRoute, Link } from "@tanstack/react-router";
import { listPublishedReports, type ReportRow } from "@/lib/reports.functions";
import { buildEnTopics, enTopicSlug } from "@/lib/en-topics";
import { TAGS } from "@/lib/tags";
import { pageTitle } from "@/lib/brand";

const EN = "https://smartsustainability.ai";
const TR = "https://akillisurdurulebilirlik.com";
const DESC =
  "Topic collections across essays, COP31 notes and climate and AI reports: data centers, energy, greenwashing, climate finance and more.";

export const Route = createFileRoute("/topics/")({
  loader: async ({ context }) => {
    const reports = (await context.queryClient.ensureQueryData({
      queryKey: ["public-reports"],
      queryFn: () => listPublishedReports(),
    })) as ReportRow[];
    const topics = buildEnTopics(reports ?? []);
    return {
      list: TAGS.filter((t) => topics.has(t.slug))
        .map((t) => ({ slug: enTopicSlug(t.slug), en: t.en, descEn: t.descEn, count: topics.get(t.slug)!.length }))
        .sort((a, b) => b.count - a.count),
    };
  },
  head: () => ({
    meta: [
      { title: pageTitle("Topics", "en") },
      { name: "description", content: DESC },
      { property: "og:title", content: "Topics — Smart Sustainability" },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${EN}/topics` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      { rel: "canonical", href: `${EN}/topics` },
      { rel: "alternate", hreflang: "en", href: `${EN}/topics` },
      { rel: "alternate", hreflang: "tr", href: `${TR}/etiket` },
      { rel: "alternate", hreflang: "x-default", href: `${TR}/etiket` },
    ],
  }),
  component: TopicsIndex,
});

function TopicsIndex() {
  const { list } = Route.useLoaderData();
  return (
    <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
      <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight">Topics</h1>
      <p className="mt-5 max-w-2xl text-ink/70 leading-relaxed">{DESC}</p>
      <ul className="mt-12 grid md:grid-cols-2 gap-6">
        {list.map((t) => (
          <li key={t.slug} className="border border-border p-6">
            <Link to="/topics/$slug" params={{ slug: t.slug }} className="group block">
              <h2 className="font-serif text-2xl group-hover:text-clay transition-colors">{t.en}</h2>
              <p className="mt-2 text-sm text-ink/70">{t.descEn}</p>
              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-ink/60">{t.count} entries</p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
